// src/services/redemptionsService.js
import { apiRequest } from "./apiService";
import { buildQueryString } from "./queryString";

const enc = (v) => encodeURIComponent(String(v == null ? "" : v));

const normalizeString = (v) => String(v == null ? "" : v).trim();

const normalizeCurrency = (v) => {
  const s = normalizeString(v).toUpperCase();
  return s || undefined;
};

const normalizeNumber = (v) => {
  if (typeof v === "number") {
    return Number.isFinite(v) ? v : undefined;
  }

  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return undefined;
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
  }

  return undefined;
};

const normalizeListParams = (params = {}) => {
  const p = { ...(params || {}) };

  const cursor =
    p.cursor ??
    p.next_cursor ??
    p.nextCursor ??
    p.last_key ??
    p.lastKey ??
    null;

  if (cursor != null && normalizeString(cursor) !== "") {
    p.last_key = cursor;
  } else {
    delete p.last_key;
  }

  delete p.cursor;
  delete p.next_cursor;
  delete p.nextCursor;
  delete p.lastKey;

  if (p.creatorId != null && p.influencerId == null) {
    p.influencerId = p.creatorId;
  }
  delete p.creatorId;

  if (p.fromDate != null && p.from_iso == null) {
    p.from_iso = p.fromDate;
  }
  if (p.toDate != null && p.to_iso == null) {
    p.to_iso = p.toDate;
  }

  if (p.from != null && p.from_iso == null) {
    p.from_iso = p.from;
  }
  if (p.to != null && p.to_iso == null) {
    p.to_iso = p.to;
  }

  delete p.fromDate;
  delete p.toDate;
  delete p.from;
  delete p.to;

  if (typeof p.q === "string") {
    p.q = p.q.trim();
  }

  if (typeof p.influencerId === "string") {
    p.influencerId = p.influencerId.trim();
  }

  if (typeof p.inviteId === "string") {
    p.inviteId = p.inviteId.trim();
  }

  if (p.limit != null) {
    const n = Number(p.limit);
    p.limit = Number.isFinite(n) ? n : undefined;
  }

  Object.keys(p).forEach((k) => {
    if (
      p[k] === undefined ||
      p[k] === null ||
      (typeof p[k] === "string" && p[k].trim() === "")
    ) {
      delete p[k];
    }
  });

  return p;
};

const normalizeCreatePayload = (payload = {}) => {
  const p = { ...(payload || {}) };

  if (p.creatorId != null && p.influencerId == null) {
    p.influencerId = p.creatorId;
  }
  delete p.creatorId;

  if (p.revenue != null && p.amount == null) {
    p.amount = p.revenue;
  }
  delete p.revenue;

  const amount = normalizeNumber(p.amount);
  if (amount !== undefined) {
    p.amount = amount;
  }

  const currency = normalizeCurrency(p.currency);
  if (currency) {
    p.currency = currency;
  } else {
    delete p.currency;
  }

  if (
    "metadata" in p &&
    p.metadata != null &&
    (typeof p.metadata !== "object" || Array.isArray(p.metadata))
  ) {
    p.metadata = { value: String(p.metadata) };
  }

  if (typeof p.orderId === "string") {
    p.orderId = p.orderId.trim();
  }

  if (typeof p.influencerId === "string") {
    p.influencerId = p.influencerId.trim();
  }

  if (typeof p.inviteId === "string") {
    p.inviteId = p.inviteId.trim();
  }

  if (typeof p.source === "string") {
    p.source = p.source.trim().toLowerCase();
  }

  if (typeof p.redeemedAt === "string") {
    p.redeemedAt = p.redeemedAt.trim();
  }

  if (typeof p.redeemMethod === "string") {
    p.redeemMethod = p.redeemMethod.trim().toLowerCase();
  }

  if (typeof p.proofType === "string") {
    p.proofType = p.proofType.trim();
  }

  Object.keys(p).forEach((k) => {
    if (
      p[k] === undefined ||
      p[k] === null ||
      (typeof p[k] === "string" && p[k].trim() === "")
    ) {
      delete p[k];
    }
  });

  return p;
};

const normalizeRedemption = (raw = {}) => {
  const redemptionId = raw.redemptionId || raw.redemption_id || raw.id || "";

  return {
    ...raw,
    id: redemptionId,
    redemptionId,
    offerId: raw.offerId || raw.offer_id || "",
    campaignId: raw.campaignId || raw.campaign_id || null,
    orderId: raw.orderId || raw.order_id || "",
    source: raw.source || "manual",
    amount:
      typeof raw.amount === "number"
        ? raw.amount
        : Number(raw.amount ?? raw.revenue ?? 0) || 0,
    currency: raw.currency || "GBP",
    redeemedAt: raw.redeemedAt || raw.redeemed_at || null,
    inviteId: raw.inviteId || raw.invite_id || null,
    influencerId:
      raw.influencerId ||
      raw.influencer_id ||
      raw.creatorId ||
      raw.creator_id ||
      null,
    metadata:
      raw.metadata && typeof raw.metadata === "object" && !Array.isArray(raw.metadata)
        ? raw.metadata
        : {},
    createdAt: raw.createdAt || raw.created_at || null,
    redeemMethod: raw.redeemMethod || raw.redeem_method || null,
    storeLocationId: raw.storeLocationId || raw.store_location_id || null,
    cashierId: raw.cashierId || raw.cashier_id || null,
    receiptId: raw.receiptId || raw.receipt_id || null,
    posTxnId: raw.posTxnId || raw.pos_txn_id || null,
    proofType: raw.proofType || raw.proof_type || null,
  };
};

const normalizeListResponse = (raw = {}) => {
  return {
    ...raw,
    items: Array.isArray(raw?.items) ? raw.items.map(normalizeRedemption) : [],
    count: Number(raw?.count || 0),
    next_cursor: raw?.next_cursor || null,
    last_key: raw?.last_key || raw?.next_cursor || null,
    pageTotalAmount:
      typeof raw?.pageTotalAmount === "number"
        ? raw.pageTotalAmount
        : Number(raw?.pageTotalAmount || 0) || 0,
    currency: raw?.currency || "GBP",
  };
};

/**
 * POST /offers/{offerId}/redemptions
 */
export const createRedemption = async (offerId, payload) => {
  if (!offerId) {
    throw new Error("offerId is required to create a redemption.");
  }
  if (!payload || typeof payload !== "object") {
    throw new Error("Valid payload is required to create a redemption.");
  }

  const res = await apiRequest(
    `/offers/${enc(offerId)}/redemptions`,
    "POST",
    normalizeCreatePayload(payload)
  );

  return normalizeRedemption(res || {});
};

/**
 * GET /offers/{offerId}/redemptions?from_iso=&to_iso=&q=&influencerId=&inviteId=&limit=&last_key=
 */
export const listRedemptions = async (offerId, params = {}) => {
  if (!offerId) {
    throw new Error("offerId is required to list redemptions.");
  }

  const qs = buildQueryString(normalizeListParams(params));
  const res = await apiRequest(`/offers/${enc(offerId)}/redemptions${qs}`, "GET");

  return normalizeListResponse(res || {});
};

export const importRedemptionsCsv = async (offerId, csvText, source = "manual") => {
  if (!offerId) {
    throw new Error("offerId is required to import redemptions.");
  }

  const text = String(csvText ?? "").trim();
  if (!text) {
    throw new Error("CSV text is required.");
  }

  const qs = buildQueryString({ source });

  return apiRequest(
    `/offers/${enc(offerId)}/redemptions/import${qs}`,
    "POST",
    text,
    {
      headers: {
        "Content-Type": "text/csv",
      },
    }
  );
};